// Mirror of CommonPdfLanguagesSkillsSection.jsx: a 6-column table with a themed
// header row, a sub-header row, and one row per language. Accent rules sit under
// the sub-header and under each language row (as in the PDF dividers).

import { AlignmentType, BorderStyle, Table, TableCell, TableRow, WidthType } from "docx";
import { CONTENT_WIDTH, NO_CELL_BORDERS, NO_TABLE_BORDERS, frac, para, run } from "../../shared/wordHelpers";
import { FONT_ARIAL } from "../../shared/wordTheme";

const FRACTIONS = [ 0.30, 0.14, 0.14, 0.14, 0.14, 0.14 ];
const COLS = FRACTIONS.map( frac );

function langCell( paragraph, { span = 1, accent, shading, valign } = {} ) {
    const spannedWidth = COLS.slice( 0, span ).reduce( ( a, b ) => a + b, 0 );
    return new TableCell( {
        width: { size: spannedWidth, type: WidthType.DXA },
        columnSpan: span,
        verticalAlign: valign,
        borders: accent
            ? { ...NO_CELL_BORDERS, bottom: { style: BorderStyle.SINGLE, size: 6, color: accent } }
            : NO_CELL_BORDERS,
        ...( shading ? { shading: { fill: shading } } : {} ),
        children: [ paragraph ],
    } );
}

function centered( text, opts ) {
    return para( run( text, opts ), { alignment: AlignmentType.CENTER, spacing: { after: 0 } } );
}

export function buildCommonLanguages( { data, props, translations, accent } ) {
    const { languageSkills } = data;
    const { documentLanguage } = props;
    const t = translations?.pdf ?? {};
    const font = FONT_ARIAL;
    const white = "FFFFFF";

    const headerRow = new TableRow( {
        children: [
            langCell( para( run( " ", { font } ), { spacing: { after: 0 } } ), { span: 1, shading: accent } ),
            langCell( centered( t.comprehensionHeader, { pt: 10, bold: true, color: white, font } ), { span: 2, shading: accent } ),
            langCell( centered( t.oralExpressionHeader, { pt: 10, bold: true, color: white, font } ), { span: 2, shading: accent } ),
            langCell( centered( t.writingExpressionHeader, { pt: 10, bold: true, color: white, font } ), { span: 1, shading: accent } ),
        ],
    } );

    const subRow = new TableRow( {
        children: [
            langCell( para( [], { spacing: { after: 0 } } ), { accent } ),
            langCell( centered( t.listeningComprehensionHeader, { pt: 6, font } ), { accent } ),
            langCell( centered( t.readingComprehensionHeader, { pt: 6, font } ), { accent } ),
            langCell( centered( t.continuousOralExpressionHeader, { pt: 6, font } ), { accent } ),
            langCell( centered( t.oralInteractionHeader, { pt: 6, font } ), { accent } ),
            langCell( para( [], { spacing: { after: 0 } } ), { accent } ),
        ],
    } );

    const dataRows = languageSkills
        .filter( skill => skill.language )
        .sort( ( a, b ) => a.order - b.order )
        .map( skill => {
            const level = key => skill[ key ]?.translations?.[ documentLanguage ] ?? "";
            return new TableRow( {
                children: [
                    langCell(
                        para( run( skill.language?.translations?.[ documentLanguage ] ?? skill.language?.code ?? "", { pt: 6, bold: true, font } ), { spacing: { after: 0 } } ),
                        { accent }
                    ),
                    langCell( centered( level( "listeningComprehensionLevel" ), { pt: 6, font } ), { accent } ),
                    langCell( centered( level( "readingComprehensionLevel" ), { pt: 6, font } ), { accent } ),
                    langCell( centered( level( "continuousOralExpressionLevel" ), { pt: 6, font } ), { accent } ),
                    langCell( centered( level( "oralInteractionLevel" ), { pt: 6, font } ), { accent } ),
                    langCell( centered( level( "writingExpressionLevel" ), { pt: 6, font } ), { accent } ),
                ],
            } );
        } );

    return new Table( {
        width: { size: CONTENT_WIDTH, type: WidthType.DXA },
        columnWidths: COLS,
        borders: NO_TABLE_BORDERS,
        rows: [ headerRow, subRow, ...dataRows ],
    } );
}
